/**
 * Tooltip text generators for the freshness-card lag readouts.
 *
 * Goal: when an operator hovers a number like `2s`, they should see WHAT the
 * number measures, HOW it is computed, and WHICH indexer field it comes from.
 *
 * Seconds-only: the block-gap (`blk`) annotation was removed from the UI — lag
 * is reported purely in seconds. Pipeline-specific so the formula matches the
 * actual extractor field.
 */

import type { Pipeline } from './lag-bars';

interface PipelineFormula {
  /** Human-readable name of the pipeline this row monitors. */
  readonly label: string;
  /** Field used for `lagSec`. */
  readonly utimeField: string;
}

const FORMULAS: Record<Pipeline, PipelineFormula> = {
  masterchain: {
    label: 'masterchain blocks',
    utimeField: 'last.gen_utime',
  },
  actions: {
    label: 'actions enrichment',
    utimeField: 'actions[0].trace_end_utime',
  },
  traces: {
    label: 'traces enrichment',
    utimeField: 'traces[0].end_utime',
  },
  transactions: {
    label: 'transactions',
    utimeField: 'transactions[0].now',
  },
  unknown: {
    label: 'pipeline',
    utimeField: 'response timestamp',
  },
};

/**
 * Tooltip for the current-lag readout (the bold number on the right of
 * each row, e.g. `2s`).
 */
export function formatCurrentLagTooltip(
  currentLagSec: number | null | undefined,
  pipeline: Pipeline,
): string {
  const f = FORMULAS[pipeline];
  const sec = formatNum(currentLagSec, 's');
  return [
    `Most recent ${f.label} freshness reading`,
    '',
    `${sec} = now − ${f.utimeField}`,
    '',
    'Staleness of the latest indexed row against wall-clock. Since Catchain 2.0' +
      ' (April 2026) TON produces blocks at ~400ms cadence with sub-second' +
      ' finality, so a few seconds here is just the gap between the block landing' +
      ' on chain and our probe reading the row.',
  ].join('\n');
}

/**
 * Tooltip for the subtitle (`p95 Xs · max Zs`) under the endpoint path.
 * Explains aggregation across the current window.
 */
export function formatSummaryTooltip(
  p95LagSec: number | null | undefined,
  maxLagSec: number | null | undefined,
  pipeline: Pipeline,
  windowLabel: string,
): string {
  const f = FORMULAS[pipeline];
  return [
    `${f.label} freshness — ${windowLabel} window aggregate`,
    '',
    'p95 / max are computed across all successful (freshness) probes in the window.',
    '',
    `lagSec = now − ${f.utimeField}`,
    '',
    `p95: ${formatNum(p95LagSec, 's')}`,
    `max: ${formatNum(maxLagSec, 's')}`,
  ].join('\n');
}

function formatNum(
  value: number | null | undefined,
  unit: string,
): string {
  if (value === null || value === undefined) return `—${unit}`;
  // Lag is a 1-decimal float end-to-end; round to 1 decimal (matching
  // formatLag elsewhere) so backend/float-arithmetic artifacts like
  // 2.4000000000000004 don't print verbatim in the tooltip. Whole values
  // drop the ".0" so "2" stays "2s", not "2.0s".
  const rounded = Math.round(value * 10) / 10;
  return `${String(rounded)}${unit}`;
}
